import { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchMe, type PortalUser } from '../../api/portal';

// Gates the portal routes behind the Salesforce session. While /api/auth/me
// resolves we show nothing; without a session we show the sign-in screen;
// with one we render children and expose the user via useUser().

const UserContext = createContext<PortalUser | null>(null);
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its provider component
export const useUser = () => useContext(UserContext);

const DENIAL_MESSAGES: Record<string, string> = {
  'org-not-allowed':
    'Your Salesforce org is not enabled for this portal. Ask Steve to add your org ID to the allowlist.',
  denied: 'Salesforce sign-in was cancelled.',
};

export default function AuthGate({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [checked, setChecked] = useState(false);
  const [params] = useSearchParams();
  const authParam = params.get('auth');

  useEffect(() => {
    fetchMe()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setChecked(true));
  }, []);

  if (!checked) {
    return <p className="px-6 py-24 text-center text-sm text-slate-400">Checking session…</p>;
  }

  if (!user) {
    return (
      <section className="px-6 py-24 max-w-md mx-auto text-center">
        <h1 className="text-2xl font-bold tracking-tight">Digadop AI portal</h1>
        <p className="mt-3 text-sm text-slate-600">
          Sign in with the same Salesforce login you use for Digadop Help.
        </p>
        {authParam && (
          <p className="mt-4 text-sm text-rose-700 border border-rose-200 bg-rose-50 rounded-lg px-4 py-3">
            {DENIAL_MESSAGES[authParam] ?? `Sign-in failed (${authParam}). Try again.`}
          </p>
        )}
        <div className="mt-8 flex flex-col gap-2 items-center">
          <a
            href="/api/auth/login"
            className="bg-blue-600 hover:bg-blue-700 text-white text-base font-medium px-6 py-3 rounded-lg shadow-sm transition-colors"
          >
            Sign in with Salesforce
          </a>
          <a href="/api/auth/login?aud=sandbox" className="text-xs text-slate-500 hover:text-slate-700">
            Sign in via a sandbox instead
          </a>
        </div>
      </section>
    );
  }

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}
